import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  ViewChild,
  ElementRef,
  CUSTOM_ELEMENTS_SCHEMA,
  AfterViewInit,
  HostListener,
  OnChanges,
  SimpleChanges
} from '@angular/core';

import {FormsModule} from '@angular/forms';
import 'cally';
import {BreakpointObserver, Breakpoints} from "@angular/cdk/layout";

export interface DatePickerResult {
  date: Date;
  formatted: string;
}

@Component({
  selector: 'ui-datepicker',
  standalone: true,
  imports: [FormsModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './ui-datepicker.component.html',
  styleUrl: './ui-datepicker.component.scss'
})
export class UiDatepickerComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() value: Date | null = null;
  @Input() placeholder = 'Select date';
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;
  @Input() disabled = false;
  @Input() size: 'sm' | 'md' | 'lg' = 'md';

  /**
   * When true, the dropdown opens automatically.
   * Useful when the parent renders the datepicker conditionally and wants it to open immediately.
   */
  @Input() autoOpen = false;

  @Output() dateSelected = new EventEmitter<DatePickerResult>();
  @Output() closed = new EventEmitter<void>();

  @ViewChild('dropdown') dropdown?: ElementRef<HTMLDetailsElement>;
  @ViewChild('dropdownContent') dropdownContent?: ElementRef<HTMLDivElement>;
  @ViewChild('calendarDate') calendarDate?: ElementRef<any>;

  selectedDate: Date | null = null;
  isOpen = false;
  isMobile = false;


  constructor(private breakpointObserver: BreakpointObserver) {
    this.breakpointObserver.observe([Breakpoints.Handset])
      .subscribe(result => {
        this.isMobile = result.matches;
        if (this.isOpen) {
          this.positionDropdown();
        }
      });
  }

  ngOnInit(): void {
    if (this.value) {
      this.selectedDate = new Date(this.value);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['autoOpen'] && this.autoOpen) {
      // Wait for the <details>/<summary> to be in the DOM.
      queueMicrotask(() => this.openDropdown());
    }

    if (changes['value'] && this.value) {
      // Keep internal state aligned when parent updates the value.
      this.selectedDate = new Date(this.value);
    }
  }

  ngAfterViewInit(): void {
    // Set up event listener for Cally calendar changes
    if (this.calendarDate?.nativeElement) {
      this.calendarDate.nativeElement.addEventListener('change', (e: any) => {
        const selectedDateStr = e.target.value;
        if (selectedDateStr) {
          const date = new Date(selectedDateStr);
          this.selectDate(date);
        }
      });
    }

    // Position dropdown on open
    if (this.dropdown?.nativeElement) {
      this.dropdown.nativeElement.addEventListener('toggle', () => {
        this.isOpen = this.dropdown!.nativeElement.open;
        if (this.isOpen) {
          this.positionDropdown();
        }
      });
    }

    // If requested, open immediately after we mount.
    if (this.autoOpen) {
      setTimeout(() => this.openDropdown(), 0);
    }
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onWindowChange(): void {
    if (this.isOpen) {
      this.positionDropdown();
    }
  }

  private positionDropdown(): void {

    if (this.isMobile) return;

    if (!this.dropdown?.nativeElement || !this.dropdownContent?.nativeElement) {
      return;
    }

    const button = this.dropdown.nativeElement.querySelector('summary');
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const content = this.dropdownContent.nativeElement;

    // Position below the button, aligned to the right
    content.style.position = 'fixed';
    content.style.top = `${rect.bottom + 8}px`;
    content.style.right = `${window.innerWidth - rect.right}px`;
    content.style.left = 'auto';
  }

  get formattedValue(): string {
    if (!this.selectedDate) return '';
    return this.formatDate(this.selectedDate);
  }

  get buttonClasses(): string {
    const classes = ['btn', 'btn-ghost'];

    if (this.size === 'sm') {
      classes.push('btn-sm');
    } else if (this.size === 'lg') {
      classes.push('btn-lg');
    }

    return classes.join(' ');
  }

  get calendarValue(): string {
    if (!this.selectedDate) return '';
    // Format as YYYY-MM-DD for Cally
    const year = this.selectedDate.getFullYear();
    const month = String(this.selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(this.selectedDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  get minDateStr(): string | undefined {
    if (!this.minDate) return undefined;
    const year = this.minDate.getFullYear();
    const month = String(this.minDate.getMonth() + 1).padStart(2, '0');
    const day = String(this.minDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  get maxDateStr(): string | undefined {
    if (!this.maxDate) return undefined;
    const year = this.maxDate.getFullYear();
    const month = String(this.maxDate.getMonth() + 1).padStart(2, '0');
    const day = String(this.maxDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  selectDate(date: Date | null): void {
    if (!date) return;

    this.selectedDate = date;
    const result: DatePickerResult = {
      date: date,
      formatted: this.formatDate(date)
    };

    this.dateSelected.emit(result);
    this.closeDropdown();
  }

  clear(): void {
    this.selectedDate = null;
    if (this.calendarDate?.nativeElement) {
      this.calendarDate.nativeElement.value = '';
    }
    this.dateSelected.emit({
      date: null as any,
      formatted: ''
    });
  }

  closeDropdown(): void {
    if (this.dropdown?.nativeElement) {
      this.dropdown.nativeElement.open = false;
    }

    document.body.style.overflow = '';
    this.closed.emit();
  }

  openDropdown(): void {
    if (this.disabled) return;
    if (this.dropdown?.nativeElement) {
      this.dropdown.nativeElement.open = true;
      this.isOpen = true;

      if (this.isMobile) {
        document.body.style.overflow = 'hidden';
      }

      this.positionDropdown();
    }
  }


  private formatDate(date: Date): string {
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${day}.${month < 10 ? '0' + month : month}.${year}`;
  }
}

